import React, { useState, useEffect } from "react";
import { axiosPrivate } from "../../../api/axios";
import CustomTextField from "../../CustomTextField";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography, Grid } from "@mui/material";
import API_URL from "../../../utils/API_URL";

const UserDialog = ({ open, onClose, userData = null, onSuccess }) => {
  const isEditMode = Boolean(userData);
  
  const [formValues, setFormValues] = useState({
    id: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    if (userData) {
      setFormValues({
        id: userData.login_id,
        name: userData.user_name,
        email: userData.email || "",
        password: "",
        confirmPassword: "",
      });
      setPasswordError("");
      setConfirmError("");
    } else {
      setFormValues({ id: "", name: "", email: "", password: "", confirmPassword: "" });
      setPasswordError("");
      setConfirmError("");
    }
  }, [userData, open]);

  const handleChange = (prop) => (event) => {
    const next = { ...formValues, [prop]: event.target.value };
    setFormValues(next);

    // 실시간 검증
    if (prop === "password") {
      if (!isEditMode || next.password.length > 0) {
        setPasswordError(next.password.length < 4 ? "비밀번호는 4자 이상이어야 합니다." : "");
      } else {
        setPasswordError("");
      }
      setConfirmError(next.confirmPassword && next.confirmPassword !== next.password ? "비밀번호가 일치하지 않습니다." : "");
    }
    if (prop === "confirmPassword") {
      setConfirmError(next.confirmPassword !== next.password ? "비밀번호가 일치하지 않습니다." : "");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { id, name, email, password, confirmPassword } = formValues;

    if (!id || !name) {
      alert('아이디와 이름은 필수입니다.');
      return;
    }

    // 생성 모드: 비밀번호 필수, 수정 모드: 비밀번호는 선택
    if (!isEditMode && password.length < 4) {
      setPasswordError("비밀번호는 4자 이상이어야 합니다.");
      return;
    }
    if (password || confirmPassword) {
      if (password.length < 4) {
        setPasswordError("비밀번호는 4자 이상이어야 합니다.");
        return;
      }
      if (password !== confirmPassword) {
        setConfirmError("비밀번호가 일치하지 않습니다.");
        return;
      }
    }

    try {
      if (isEditMode) {
        await axiosPrivate.post(`${API_URL}/auth/admin/updateUser`, {
          login_id: id,
          user_name: name,
          email,
          password: password || undefined,
        });
      } else {
        await axiosPrivate.post(`${API_URL}/auth/admin/register`, {
          id,
          user_name: name,
          email,
          password,
        });
      }
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        alert('이미 존재하는 로그인 아이디입니다.');
      } else if (err.response?.data) {
        alert(err.response.data);
      } else {
        console.error(err);
        alert(`유저 ${isEditMode ? '수정' : '생성'} 오류`);
      }
    }
  };

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 8 }}>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {isEditMode ? '유저 수정' : '유저 생성'}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {isEditMode ? '유저 정보 수정' : '유저 정보 입력'}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                size="small"
                name="id"
                value={formValues.id}
                autoComplete="off"
                label="아이디"
                onChange={handleChange("id")}
                placeholder="아이디"
                required
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField 
                fullWidth 
                size="small"
                name="name" 
                value={formValues.name} 
                autoComplete="off" 
                label="사용자 이름" 
                placeholder="사용자 이름" 
                onChange={handleChange("name")} 
                required 
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField 
                fullWidth 
                size="small"
                type="email" 
                label="Email" 
                placeholder="ese-tech@ese-tech.com" 
                value={formValues.email} 
                onChange={handleChange("email")} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                size="small"
                type="password"
                label="비밀번호"
                value={formValues.password}
                onChange={handleChange("password")}
                placeholder={isEditMode ? "비밀번호 변경 시에만 입력" : "비밀번호"}
                required={!isEditMode}
                error={Boolean(passwordError)}
                helperText={passwordError || (isEditMode ? "" : "최소 4자 이상")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                size="small"
                type="password"
                label="비밀번호 확인"
                value={formValues.confirmPassword}
                onChange={handleChange("confirmPassword")}
                placeholder={isEditMode ? "비밀번호 변경 시에만 입력" : "비밀번호 확인"}
                required={!isEditMode}
                error={Boolean(confirmError)}
                helperText={confirmError || ""}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 8, pt: 0 }}>
          <Button variant="contained" type="submit">
            {isEditMode ? '수정' : '생성'}
          </Button>
          <Button variant="tonal" color="secondary" onClick={onClose}>
            취소
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserDialog; 